"use client";

/** Renders loading, error, and success states for query-backed content. */

import { Button, Spinner } from "@decibeltrade/web-core/components";
import { QueryErrorResetBoundary, UseQueryResult } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { ReactNode, Suspense } from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { css, cx } from "styled-system/css";
import { center } from "styled-system/patterns";

import { getIsFetchError } from "@/utils/error";

export interface HandleQueryProps<TData, TError> {
  /** The query to handle (return from useQuery) */
  query: UseQueryResult<TData, TError>;
  /** The ReactNode to render while the query is loading */
  loading: ReactNode;
  /** The ReactNode to render when the query throws an error */
  error: ReactNode | ((error: TError) => ReactNode);
  /** The ReactNode to render when the query is disabled (defaults to loading state) */
  disabled?: ReactNode;
  /** The ReactNode to render when the query is successful */
  children: (data: TData) => ReactNode;
}

export function HandleQuery<TData, TError>({
  query,
  loading,
  error,
  disabled = loading,
  children,
}: HandleQueryProps<TData, TError>) {
  if (query.isError) {
    return typeof error === "function" ? error(query.error) : error;
  }

  if (query.isLoading) {
    return loading;
  }

  if (query.data === undefined) {
    return disabled;
  }

  return children(query.data);
}

export interface HandleSuspenseQueryProps {
  /** The ReactNode to render while the query is loading */
  loading: React.ReactNode;
  /** The ReactNode to render when the query throws an error */
  error: (props: FallbackProps) => ReactNode;
  /** The ReactNode to render when the query is successful */
  children: React.ReactNode;
}

export function HandleSuspenseQuery(props: HandleSuspenseQueryProps) {
  return (
    <Suspense fallback={props.loading}>
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary onReset={reset} fallbackRender={props.error}>
            {props.children}
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </Suspense>
  );
}

export interface DefaultLoadingProps {
  className?: string;
}

export function DefaultLoading({ className }: DefaultLoadingProps) {
  return (
    <div className={cx(center(), className)}>
      <Spinner />
    </div>
  );
}

export interface DefaultErrorProps extends FallbackProps {
  direction?: "horizontal" | "vertical";
  className?: string;
  resetQueryParams?: boolean;
}

export function DefaultError({
  direction = "vertical",
  className,
  resetErrorBoundary,
  error,
  resetQueryParams,
}: DefaultErrorProps) {
  const router = useRouter();
  const t = useTranslations("general");
  const isFetchError = getIsFetchError(error);

  return (
    <div
      data-testid="error-boundary"
      className={cx(
        center({ gap: "16", flexDirection: direction === "vertical" ? "column" : "row" }),
        className,
      )}
    >
      <span className={css({ textStyle: "body.md", color: "text.secondary" })}>
        {isFetchError ? t("fetchError") : t("unexpectedError")}
      </span>
      <Button
        onClick={() => {
          if (resetQueryParams) {
            router.replace(location.pathname);
          }
          resetErrorBoundary();
        }}
      >
        {t("tryAgain")}
      </Button>
    </div>
  );
}
